// ----------------------------------------------------------------------- imports externos
import { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";

// ----------------------------------------------------------------------- imports internos
import { diaHoraCorta } from "../shared/fechaAr";
import { fotoUrl, marcarLectura, traerLecturas } from "../services/lecturas";
import "./css/lecturas.css";
import PuertaCerrada from "../components/funcionales/PuertaCerrada";

/* Lecturas de vales: la foto contra lo que entendió el bot.
 *
 * ── POR QUÉ EXISTE ──────────────────────────────────────────────────────────
 *
 * El que saca la foto (portería) no ve lo que el bot leyó, y el que puede
 * juzgar la lectura (Igna) no ve la foto. Esta pantalla junta las dos cosas:
 * la imagen del vale al lado de lo que el modelo leyó (`leido`) y de lo que
 * quedó guardado — y si difieren, la diferencia YA es información: alguien
 * corrigió a mano lo que el bot entendió mal.
 *
 * ── EL VEREDICTO ES EL DATASET ──────────────────────────────────────────────
 *
 * Lo que se marca queda guardado en la carga. De ahí salen las reglas nuevas
 * del prompt (la de los ceros a la izquierda nació exactamente así, de tres
 * duplicados reales). Revisar acá ES el entrenamiento — no hace falta que
 * portería corrija nada.
 *
 * ── Y SON TRES, NO DOS ──────────────────────────────────────────────────────
 *
 * «Leyó mal» y «No leyó» son dos problemas distintos y arreglarlos cuesta
 * cosas distintas: uno es una regla en el prompt, el otro es la foto que ni
 * llegó a lectura (borrosa, mal recortada, o la llamada que falló). Metidos
 * en el mismo «mal», el dataset dice «el OCR falla» sin decir en qué — que es
 * justo lo que esta pantalla existe para evitar (Igna, 21/08/2026).
 *
 * Sólo «Leyó mal» pide nota: en «no leyó» no hay nada que el bot haya
 * entendido mal, así que no hay nada que corregirle.
 */


/* «30/09 14:32», o «30/09 · s/hora» cuando el vale no trae hora: el bot lo
   guarda a las 12:00:00 en punto y mostrarlo así sería inventar una hora
   (Nico, 01/10). Ver `sinHora`. */
const cuando = (iso) => diaHoraCorta(iso);

/* Los campos del JSON del modelo que vale la pena enfrentar con la fila.
 * El resto (importe, retiro, km…) se lista aparte, sin comparación: no se
 * guarda en columnas propias y no hay contra qué. */
const COMPARADOS = [
  ["vale", "Vale Nº"],
  // El manuscrito de arriba a la derecha es el ODÓMETRO, no otro vale
  // (Igna, 21/08/2026): el modelo lo devuelve en `km_manual` y la fila lo
  // guarda en `km`.
  ["km", "Odómetro"],
  ["interno", "Interno"],
  ["litros", "Litros"],
];

const norm = (v) =>
  String(v ?? "").trim().toUpperCase().replace(/^0+(?=\d)/, "");

export const difiere = (leido, guardado) => {
  if (leido == null || leido === "") return false; // sin lectura no hay pelea
  return norm(leido) !== norm(guardado);
};

const Lecturas = () => {
  const { permisos = null } = useOutletContext() || {};
  const permitido = permisos !== null && permisos.includes("ver.lecturas");

  const [datos, setDatos] = useState(undefined);
  const [error, setError] = useState(false);
  const [filtro, setFiltro] = useState("sin"); // sin | falla | todas
  const [fotos, setFotos] = useState({});      // id -> url firmada
  const [notas, setNotas] = useState({});      // id -> nota en curso
  const [abiertoMal, setAbiertoMal] = useState(null); // id con la nota abierta
  const [estado, setEstado] = useState("");

  const cargar = () => {
    traerLecturas()
      .then((filas) => {
        setDatos(filas);
        // Las firmas se piden todas juntas al entrar: son una llamada liviana
        // por foto y la pantalla es de una persona, no de un turno entero.
        filas.forEach((f) => {
          if (f.foto)
            fotoUrl(f.foto).then((u) =>
              setFotos((prev) => ({ ...prev, [f.id]: u })));
        });
      })
      .catch(() => setError(true));
  };

  useEffect(() => {
    if (!permitido) return;
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permitido]);

  const visibles = useMemo(() => {
    if (!datos) return [];
    if (filtro === "sin") return datos.filter((f) => !f.lectura_estado);
    // «Lo que salió mal» junta los dos veredictos negativos a propósito: para
    // arreglar hay que mirar todo lo que falló, y separarlos en dos pestañas
    // obligaría a acordarse de abrir las dos.
    if (filtro === "falla")
      return datos.filter((f) => f.lectura_estado === "mal" ||
                                 f.lectura_estado === "no_leyo");
    return datos;
  }, [datos, filtro]);

  const sinRevisar = (datos || []).filter((f) => !f.lectura_estado).length;

  const marcar = async (f, veredicto) => {
    try {
      // La nota es sólo del «leyó mal»: en los otros dos no hay nada que
      // corregirle al bot, y arrastrar lo tipeado ensuciaría el dataset.
      await marcarLectura(f.id, veredicto,
                          veredicto === "mal" ? notas[f.id] || "" : "");
      setAbiertoMal(null);
      setEstado("");
      cargar();
    } catch (e) {
      setEstado(e?.message || "No se pudo guardar el veredicto.");
    }
  };

  if (permisos !== null && !permitido) {
    return <PuertaCerrada />;
  }

  if (error) {
    return (
      <div className="panel-aviso"><h3>No se pudo consultar</h3></div>
    );
  }

  return (
    <>
      <div className="urea-barra">
        <div className="urea-periodos" role="group" aria-label="Filtro">
          {[["sin", `Sin revisar${sinRevisar ? ` (${sinRevisar})` : ""}`],
            ["falla", "Lo que salió mal"],
            ["todas", "Todas"]].map(([id, label]) => (
            <button
              key={id}
              type="button"
              className={"urea-seg" + (filtro === id ? " activo" : "")}
              onClick={() => setFiltro(id)}
            >
              {label}
            </button>
          ))}
        </div>
        {estado && <span className="pill mal">{estado}</span>}
      </div>

      {datos !== undefined && visibles.length === 0 && (
        <div className="panel-aviso suave">
          <h3>
            {filtro === "sin"
              ? "No queda ninguna lectura sin revisar"
              : "Nada para mostrar con este filtro"}
          </h3>
        </div>
      )}

      {visibles.map((f) => {
        const l = f.leido || null;
        return (
          <div className="lec-carta" key={f.id}>
            <div className="lec-foto">
              {fotos[f.id] ? (
                <a href={fotos[f.id]} target="_blank" rel="noreferrer">
                  <img src={fotos[f.id]} alt={`Vale de la carga ${f.id}`} />
                </a>
              ) : f.foto ? (
                <span className="tenue">Cargando la foto…</span>
              ) : (
                <span className="tenue">
                  Sin foto guardada (carga vieja o el storage falló ese día).
                </span>
              )}
            </div>

            <div className="lec-datos">
              <div className="lec-cabeza">
                <b>{cuando(f.fecha)}</b> · interno {f.interno || "—"} ·{" "}
                {f.quien_nombre || "¿?"}
                {f.lectura_estado === "bien" && (
                  <span className="pill gris">✓ revisada: leyó bien</span>
                )}
                {f.lectura_estado === "mal" && (
                  <span className="pill mal">✗ leyó mal</span>
                )}
                {f.lectura_estado === "no_leyo" && (
                  <span className="pill mal">✗ no leyó</span>
                )}
              </div>

              <table className="lec-tabla">
                <thead>
                  <tr>
                    <th></th>
                    <th>El bot leyó</th>
                    <th>Quedó guardado</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARADOS.map(([k, rotulo]) => {
                    const vLeido = l ? l[k] : null;
                    const vFila = f[k];
                    const mal = l && difiere(vLeido, vFila);
                    return (
                      <tr key={k} className={mal ? "lec-difiere" : ""}>
                        <td className="lec-rotulo">{rotulo}</td>
                        <td>
                          {l ? (
                            vLeido ?? <span className="tenue">no leyó</span>
                          ) : (
                            <span className="tenue">sin registro</span>
                          )}
                        </td>
                        <td>
                          {vFila ?? <span className="tenue">—</span>}
                          {mal && (
                            <span className="lec-marca">
                              {" "}corregido a mano
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {l && (l.dudas || []).length > 0 && (
                <p className="lec-dudas">
                  El bot dudó de: <b>{l.dudas.join(", ")}</b>
                </p>
              )}
              {!l && (
                <p className="lec-dudas tenue">
                  De esta carga no quedó el registro de la lectura — o se
                  tipeó a mano, o es anterior a que se guardara. Si hay foto y
                  el bot no entendió nada de ella, es un <b>«No leyó»</b>.
                </p>
              )}
              {f.lectura_nota && (
                <p className="lec-nota-vieja">Tu nota: {f.lectura_nota}</p>
              )}

              <div className="lec-acciones">
                <button
                  type="button"
                  className="tk-boton tk-boton--chico"
                  onClick={() => marcar(f, "bien")}
                >
                  ✓ Leyó bien
                </button>
                <button
                  type="button"
                  className="tk-boton tk-boton--chico tk-boton--flojo"
                  onClick={() =>
                    setAbiertoMal(abiertoMal === f.id ? null : f.id)
                  }
                >
                  ✗ Leyó mal…
                </button>
                {/* Sin puntos suspensivos y sin nota: no hay nada que el bot
                    haya entendido mal, así que no hay nada que escribirle. */}
                <button
                  type="button"
                  className="tk-boton tk-boton--chico tk-boton--flojo"
                  onClick={() => marcar(f, "no_leyo")}
                >
                  ∅ No leyó
                </button>
                {abiertoMal === f.id && (
                  <>
                    <input
                      type="text"
                      placeholder="Qué decía el papel (ej.: vale 9150, no 09150)"
                      value={notas[f.id] || ""}
                      onChange={(e) =>
                        setNotas((prev) => ({ ...prev, [f.id]: e.target.value }))
                      }
                      aria-label="Qué decía el papel"
                    />
                    <button
                      type="button"
                      className="tk-boton tk-boton--chico"
                      onClick={() => marcar(f, "mal")}
                    >
                      Guardar
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
};

export default Lecturas;
